// RecentLeads - Table of recent leads
import { Paper, Title, Table, Badge, Text, Button, Group, ActionIcon } from '@mantine/core';
import { IconEye, IconPhone, IconMail } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { Lead } from '@/types/models/lead.types';
import { ROUTES } from '@/constants/routes';

interface RecentLeadsProps {
  leads: Lead[];
  isLoading?: boolean;
}

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    new: 'blue',
    contacted: 'cyan',
    qualified: 'yellow',
    converted: 'green',
    lost: 'red',
    archived: 'gray',
  };
  return colors[status] || 'gray';
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';
  if (score >= 40) return 'orange';
  return 'red';
};

export const RecentLeads = ({ leads, isLoading }: RecentLeadsProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={3} mb="md">
          Recent Leads
        </Title>
        <Text c="dimmed">Loading leads...</Text>
      </Paper>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={3} mb="md">
          Recent Leads
        </Title>
        <Text c="dimmed">No leads yet. Create your first lead!</Text>
      </Paper>
    );
  }

  const rows = leads.slice(0, 10).map((lead) => (
    <Table.Tr key={lead.id}>
      <Table.Td>
        <Text fw={500}>{lead.customer_name}</Text>
        <Text size="xs" c="dimmed">
          {lead.email || lead.phone || 'No contact'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{lead.car_interest || '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={getScoreColor(lead.score)} variant="light">
          {lead.score}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={getStatusColor(lead.status)} variant="light">
          {lead.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{lead.source}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {new Date(lead.created_at).toLocaleDateString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon
            variant="light"
            color="blue"
            onClick={() => navigate(ROUTES.LEAD_DETAILS(lead.id))}
          >
            <IconEye size={16} />
          </ActionIcon>
          {lead.phone && (
            <ActionIcon variant="light" color="green">
              <IconPhone size={16} />
            </ActionIcon>
          )}
          {lead.email && (
            <ActionIcon variant="light" color="cyan">
              <IconMail size={16} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="md">
        <Title order={3}>Recent Leads</Title>
        <Button variant="light" size="sm" onClick={() => navigate(ROUTES.LEADS)}>
          View All
        </Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Customer</Table.Th>
            <Table.Th>Car Interest</Table.Th>
            <Table.Th>Score</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Source</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </Paper>
  );
};
